/* Haavi Cart Drawer - <cart-drawer>.
   A global slide-out cart. Opens from any [data-cart-drawer-open] trigger (the header cart icon),
   from the Escape key / overlay / close button to close, and auto-opens when a product is added
   (the 'cart:added' event dispatched by assets/ho-products.js).

   Quantity steppers, the quantity input and remove run through Shopify's AJAX cart
   (POST /cart/change.js) with the `sections` param, then swap [data-cart-drawer-contents] in place
   with Shopify's server-rendered markup, so line totals, subtotal, the count pill, the free-ship
   bar and the empty state are all server-computed (no JS money formatting). Interactions are
   delegated on the root so they survive the swap. Every change also dispatches Horizon's standard
   CartLinesUpdateEvent (same recipe as ho-cart.js) so the header count + any other cart components
   stay in sync; the custom header badge is updated directly as a fallback. Rapid stepper clicks are
   debounced per line. Dependency-free, guarded against double-init. */
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._busy = false;
      this._timers = {};
      this._lastFocus = null;

      this.sectionId = this.dataset.sectionId || 'ho-cart-drawer';
      this.changeUrl = this._js(this.dataset.changeUrl || '/cart/change');
      this.cartUrl = this.dataset.cartUrl || '/cart';

      // Global open triggers (header cart icon etc.) live OUTSIDE this element.
      this._onDocClick = (e) => {
        const opener = e.target.closest && e.target.closest('[data-cart-drawer-open]');
        if (opener) { e.preventDefault(); this.open(); }
      };
      document.addEventListener('click', this._onDocClick);

      // Auto-refresh + open when a product is added elsewhere on the page.
      this._onAdded = () => this._refreshAndOpen();
      document.addEventListener('cart:added', this._onAdded);

      // Delegated interactions inside the drawer (survive the contents swap).
      this.addEventListener('click', (e) => this._onClick(e));
      this.addEventListener('change', (e) => this._onFieldChange(e));

      // Escape closes when open; Tab is trapped inside the panel (aria-modal containment).
      this._onKeydown = (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key === 'Tab') this._trapTab(e);
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    _js(url) { return /\.js($|\?)/.test(url) ? url : url + '.js'; }

    /* ---------- open / close ---------- */
    open() {
      if (this.classList.contains('is-open')) return;
      this._lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
      const panel = this.querySelector('[data-cart-drawer-panel]');
      const close = this.querySelector('[data-cart-drawer-close]');
      if (panel) panel.inert = false; // make the panel focusable / AT-visible while open
      // Move focus into the panel (prefer the close button, else the panel itself).
      window.requestAnimationFrame(() => { (close || panel || this).focus(); });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      const panel = this.querySelector('[data-cart-drawer-panel]');
      if (panel) panel.inert = true;  // remove off-canvas controls from focus / AT while closed
      // Restore focus to whatever opened the drawer.
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        try { this._lastFocus.focus(); } catch (e) {}
      }
      this._lastFocus = null;
    }

    /* ---------- delegated clicks ---------- */
    _onClick(e) {
      if (e.target.closest('[data-cart-drawer-close]')) { e.preventDefault(); this.close(); return; }
      if (e.target.closest('[data-cart-drawer-overlay]')) { this.close(); return; }

      const rem = e.target.closest('[data-cart-remove]');
      if (rem) { e.preventDefault(); this._queue(rem.getAttribute('data-line-key'), 0, rem.closest('[data-line-item]')); return; }

      const plus = e.target.closest('[data-cart-plus]');
      const minus = e.target.closest('[data-cart-minus]');
      if (!plus && !minus) return;
      const row = (plus || minus).closest('[data-line-item]');
      const input = row && row.querySelector('[data-cart-qty]');
      if (!input) return;
      let v = parseInt(input.value, 10);
      if (isNaN(v)) v = 1;
      const next = plus ? v + 1 : Math.max(1, v - 1); // floor at 1; removal is only via the X control
      if (next === v) return;                          // no-op guard (minus at qty 1)
      input.value = next;                              // optimistic UI
      this._queue(input.getAttribute('data-line-key'), next, row);
    }

    /* ---------- direct edits to the quantity input ---------- */
    _onFieldChange(e) {
      const input = e.target.closest && e.target.closest('[data-cart-qty]');
      if (!input) return;
      let v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;   // floor at 1; removal is only via the X control
      input.value = v;
      this._queue(input.getAttribute('data-line-key'), v, input.closest('[data-line-item]'));
    }

    /* ---------- debounce rapid changes per line ---------- */
    _queue(key, qty, row) {
      if (!key) return;
      if (row) row.setAttribute('aria-busy', 'true');
      clearTimeout(this._timers[key]);
      this._timers[key] = setTimeout(() => this._change(key, qty), 260);
    }

    /* ---------- AJAX cart change + section re-render ---------- */
    async _change(key, quantity) {
      if (this._busy) { this._queue(key, quantity, null); return; } // retry once free
      this._busy = true;
      this.setAttribute('aria-busy', 'true');

      const prevFill = this.querySelector('[data-ship-fill]');
      const prevFillW = prevFill ? prevFill.style.width : null;

      let Events = null;
      try { Events = await import('@shopify/events'); } catch (e) { Events = null; }
      let deferred = null;

      try {
        if (Events && Events.CartLinesUpdateEvent) {
          deferred = Events.CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(new Events.CartLinesUpdateEvent({
            action: quantity === 0 ? 'remove' : 'change',
            context: 'cart',
            lines: [{ id: String(key), quantity: quantity }],
            promise: deferred.promise,
          }));
        }

        const res = await fetch(this.changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(key), quantity: quantity }),
          credentials: 'same-origin',
        });
        const cart = await res.json();
        if (cart.status && cart.status >= 400) throw new Error(cart.message || 'Cart change failed');

        // Re-render the drawer. ho-cart-drawer is a LAYOUT section ({% section %} in theme.liquid),
        // which the multi-section API (?sections= / the cart `sections` param) does NOT render (returns 404).
        // The single-section API (?section_id=) does render it, so fetch that HTML and swap it in.
        const html = await this._fetchDrawerHtml();
        this._swap(html, prevFillW);
        this._syncHeader(cart.item_count);

        if (deferred && Events) {
          try {
            deferred.resolve({
              cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { items: cart.items, sections: cart.sections, source: 'ho-cart-drawer', didError: false },
            });
          } catch (e) { /* noop */ }
        }
      } catch (err) {
        if (deferred) { try { deferred.resolve({ cart: null, detail: { source: 'ho-cart-drawer', didError: true } }); } catch (e) {} }
        console.warn('[ho-cart-drawer] cart change failed', err);
        // Fallback: send the shopper to the full cart page so they still see the correct state.
        window.location = this.cartUrl;
      } finally {
        this._busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    /* ---------- fetch the freshly-rendered drawer HTML (single-section API) ----------
       ho-cart-drawer is rendered in theme.liquid via {% section %}, so it is NOT addressable by the
       multi-section API (?sections= returns 404). ?section_id= renders one section by handle and works. */
    async _fetchDrawerHtml() {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error('drawer section fetch ' + res.status);
      return res.text();
    }

    /* ---------- refresh contents (fresh cart), then open ---------- */
    async _refreshAndOpen() {
      try {
        const prevFillW = this.querySelector('[data-ship-fill]') ? this.querySelector('[data-ship-fill]').style.width : null;
        const html = await this._fetchDrawerHtml();
        this._swap(html, prevFillW);
        const fresh = this.querySelector('[data-cart-drawer-contents]');
        if (fresh && fresh.dataset.cartCount != null) this._syncHeader(fresh.dataset.cartCount);
      } catch (err) {
        console.warn('[ho-cart-drawer] refresh failed', err);
      }
      this.open();
    }

    /* ---------- swap [data-cart-drawer-contents] innerHTML ---------- */
    _swap(html, prevFillW) {
      const contents = this.querySelector('[data-cart-drawer-contents]');
      if (!contents || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-contents]');
      if (!fresh) return;
      contents.innerHTML = fresh.innerHTML;
      if (fresh.dataset.cartCount != null) contents.dataset.cartCount = fresh.dataset.cartCount;

      // Animate the free-shipping fill from its previous width to the new target.
      const fill = this.querySelector('[data-ship-fill]');
      if (fill) {
        const target = fill.style.width;
        if (prevFillW != null && prevFillW !== target) {
          fill.style.width = prevFillW;
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => { fill.style.width = target; }));
        }
      }
    }

    /* ---------- trap Tab within the open panel (keeps the aria-modal promise) ---------- */
    _trapTab(e) {
      const panel = this.querySelector('[data-cart-drawer-panel]');
      if (!panel) return;
      const sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
      const list = Array.from(panel.querySelectorAll(sel)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    _syncHeader(count) {
      document.querySelectorAll('.ho-hdr__count').forEach((el) => { el.textContent = count; });
    }
  });
}
